import { CommonModule } from "@angular/common";
import { ModuleWithProviders, NgModule } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { Observable, of } from "rxjs";
import {
    SLIDE_SYNC_CHECK_IS_FAVORITE_SERVICE,
    SLIDE_SYNC_NAVIGATION_PREVIEW_ENABLED,
} from "./injection-tokens";
import { PreviewScreenEffects } from "./effects/preview-screen.effects";
import { EffectsModule } from "@ngrx/effects";
import { HTTP_SERVICE_URL } from "@pcp/http";
// eslint-disable-next-line @nx/enforce-module-boundaries -- known umbrella-level cycle, inversion planned: the preview screen must mount extension apps via a registry token instead of importing the integration lib (docs/architecture-review-classroom.md F6/R6)
import { ExtensionAppIntegrationModule } from "extension-apps/integration";
import { TeacherPreviewScreenModule } from "./preview/teacher-preview-screen.module";
import { ICheckIsFavoriteService } from "shared/classroom/preview-screen/core";
import { SlideType } from "@pcp/core/interfaces";
import {
    provideBeamerConfig,
    provideCommonConfig,
    provideLiveLessonModeratorConfig,
    provideLiveLessonStudentConfig,
    providePcpConfig,
    provideTeacherConfig,
    provideTutorConfig,
} from "./config/provide-config";
import { provideModuleConfig, SharedClassroomPreviewScreenFeatureShellModuleConfig } from "./config/module-config";
import { SharedClassroomDataAccessModule } from "shared/classroom/data-access";

class IsFavoriteService implements ICheckIsFavoriteService {
    isSlideAddedToFavorites(
        slideIdAndType$: Observable<{ id: number; type: SlideType } | undefined>,
    ): Observable<boolean> {
        return of(false);
    }
}

@NgModule({
    declarations: [],
    imports: [
        CommonModule,
        MatIconModule,
        TeacherPreviewScreenModule,
        // PcpSlidesModule,    // <-- RubberElement
        // PcpStreamingModule, // <-- VideoPlayer
        SharedClassroomDataAccessModule,
        EffectsModule.forFeature([PreviewScreenEffects]),
        ExtensionAppIntegrationModule,
    ],
    exports: [TeacherPreviewScreenModule],
    providers: [
        { provide: HTTP_SERVICE_URL, useValue: '' },

        // Navigation preview is always disabled by default as it's only needed in beamer apps.
        // Provide the Token in the beamer apps explicitly.
        { provide: SLIDE_SYNC_NAVIGATION_PREVIEW_ENABLED, useValue: of(false) },

        // default isFavoriteService will always return `false`
        {
            provide: SLIDE_SYNC_CHECK_IS_FAVORITE_SERVICE,
            useValue: new IsFavoriteService(),
        },
    ],
})
export class SharedClassroomPreviewScreenFeatureShellModule {
    public static forPcpTeacher(
        config: SharedClassroomPreviewScreenFeatureShellModuleConfig,
    ): ModuleWithProviders<SharedClassroomPreviewScreenFeatureShellModule> {
        return {
            ngModule: SharedClassroomPreviewScreenFeatureShellModule,
            providers: [provideCommonConfig(), providePcpConfig(), provideTeacherConfig(), provideModuleConfig(config)],
        };
    }

    public static forPcpBeamer(
        config: SharedClassroomPreviewScreenFeatureShellModuleConfig,
    ): ModuleWithProviders<SharedClassroomPreviewScreenFeatureShellModule> {
        return {
            ngModule: SharedClassroomPreviewScreenFeatureShellModule,
            providers: [provideCommonConfig(), providePcpConfig(), provideBeamerConfig(), provideModuleConfig(config)],
        };
    }

    public static forPcpLiveLessonModerator(
        config: SharedClassroomPreviewScreenFeatureShellModuleConfig,
    ): ModuleWithProviders<SharedClassroomPreviewScreenFeatureShellModule> {
        return {
            ngModule: SharedClassroomPreviewScreenFeatureShellModule,
            providers: [
                provideCommonConfig(),
                providePcpConfig(),
                provideLiveLessonModeratorConfig(),
                provideModuleConfig(config),
            ],
        };
    }

    public static forPcpLiveLessonStudent(
        config: SharedClassroomPreviewScreenFeatureShellModuleConfig,
    ): ModuleWithProviders<SharedClassroomPreviewScreenFeatureShellModule> {
        return {
            ngModule: SharedClassroomPreviewScreenFeatureShellModule,
            providers: [
                provideCommonConfig(),
                providePcpConfig(),
                provideLiveLessonStudentConfig(),
                provideModuleConfig(config),
            ],
        };
    }

    public static forTutTeacher(
        config: SharedClassroomPreviewScreenFeatureShellModuleConfig,
    ): ModuleWithProviders<SharedClassroomPreviewScreenFeatureShellModule> {
        return {
            ngModule: SharedClassroomPreviewScreenFeatureShellModule,
            providers: [
                provideCommonConfig(),
                provideTutorConfig(),
                provideTeacherConfig(),
                provideModuleConfig(config),
            ],
        };
    }

    public static forTutBeamer(
        config: SharedClassroomPreviewScreenFeatureShellModuleConfig,
    ): ModuleWithProviders<SharedClassroomPreviewScreenFeatureShellModule> {
        return {
            ngModule: SharedClassroomPreviewScreenFeatureShellModule,
            providers: [
                provideCommonConfig(),
                provideTutorConfig(),
                provideBeamerConfig(),
                provideModuleConfig(config),
            ],
        };
    }
}
